/*
A JavaScript-based Fallback for using the "cqw" unit in container queries.
Specifically implemented for Safari < v17, which has an annoying Container Query unit related bug:

Assume the following HTML:
<div style="container: outer / inline-size">
  <div style="container: inner / inline-size">
    <div class="content">...</div>
  </div>
</div>

And this SCSS code:
@container inner (width > 50cqw) {
  .content { ... }
}

Chrome/Firefox will (correctly) assume that 50cqw are 50% of the surrounding 'outer' container's width.
Safari however will use 50% of the document's width, no matter if there is an ancestor container.

------

Example: cqw Container Queries without fallback

Just how it's supposed to work.

HTML:
<div class="outer">
  <div class="inner">
    <div class="content">...</div>
  </div>
</div>

SCSS:
.outer { container: outer / inline-size; }
.inner { container: inner / inline-size; }

.content {
  @container inner (width >= 50cqw) {
    // .inner is at least half width of .outer
  }
}

------

Example: cqw Container Queries with JavaScript fallback only

Every container needs to be marked up with its container name as the [data-cqw-container] attribute.
Multiple names can be delimited by spaces. Just as in CSS, container names are optional,
the attribute value may be empty.

Also each element *querying* a container must be marked up in the [data-cqw-query] attribute.
It declares, in this particular order:
- the container name it refers to, followed by a slash (optional)
- the operator to use: "gte:", "gt:", "lte:", "lt:" or "eq:" (optional, defaults to "gte:")
- the cqw value it queries (mandatory)
Multiple queries can be delimited by spaces, e.g.: data-cqw-query="50 mycontainer/80 othercontainer/lt:33.333",
which would declare equivalents for the following queries:
@container (width >= 50cqw) { ... }
@container mycontainer (width >= 80cqw) { ... }
@container othercontainer (width < 33.333cqw) { ... }

HTML:
<div class="outer" data-cqw-container="outer">
  <div class="inner" data-cqw-container="inner">
    <div class="content" data-cqw-query="inner/50">...</div>
  </div>
</div>

Now, the JavaScript fallback code will use the described attributes to attach matcher attributes
for each combination of container name, operator and cqw value, whenever the container matches:

SCSS:
.content {
  &:where([data-cqw-match\:inner\:gte\:50]) {
    // .inner is at least half width of .outer
  }
}

------

Example: cqw Container Queries with combined approach

The JavaScript fallback is only used when `initCqwFallback` is initialized, so that should only
be called when Safari 16 and earlier is detected. The `cqw` Sass mixin can be found
in the breakpoints.scss file.

HTML:
<div class="outer" data-cqw-container="outer">
  <div class="inner" data-cqw-container="inner">
    <div class="content" data-cqw-query="inner/50">...</div>
  </div>
</div>

SCSS:
.outer { container: outer / inline-size; }
.inner { container: inner / inline-size; }

.content {
  @include cqw(50, $name: 'inner') {
    // .inner is at least half width of .outer
  }
}

*/

import { derived, inject, provide, signal, watch } from '@js/alpine/signals.js'
import { select } from '@js/util/select.js'

/**
 * @type {WeakMap<Element, { get: () => number, set: (value: number) => void }>}
 */
const elementContentWidths = new WeakMap()

// When an element size changes, update the according signal in the `elementContentWidths` registry
const observer = new ResizeObserver(entries => {
  for (let entry of entries) {
    const width = elementContentWidths.get(entry.target)
    width.set(entry.contentRect.width)
  }
})

/**
 * @param {Element} element
 */
function observeElementSize(element) {
  // Abort if element is already being observed
  if (elementContentWidths.has(element)) return

  // Get initial content width
  const styles = getComputedStyle(element)
  const contentWidth =
    element.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight)

  // Make width available as a signal through the `elementContentWidths` registry
  const [get, set] = signal(contentWidth)
  elementContentWidths.set(element, { get, set })

  // Observe element for size changes
  observer.observe(element)
}

// Run the fallback
export function initCqwFallback() {
  // Mark the fallback as active to signal to the `cqw` Sass mixin that
  // the HTML attribute strategy should be used for detecting cqw sizes.
  document.body.classList.add('cqw-fallback')

  // The window is the root container
  const [windowWidth, setWindowWidth] = signal(window.innerWidth)
  window.addEventListener('resizeEnd', () => {
    setWindowWidth(window.innerWidth)
  })
  provide(document.documentElement, 'cqw:width/', windowWidth)
  provide(document.documentElement, 'cqw:percentage/', 100)

  // Handle container elements
  select('[data-cqw-container]', containerElement => {
    observeElementSize(containerElement)

    const containerAttribute = containerElement.dataset.cqwContainer

    // Container names on this element (empty string is for "no name")
    const names = ['']
    if (containerAttribute.trim().length > 0) {
      names.push(
        ...containerAttribute
          .trim()
          .split(' ')
          .map(name => name.trim()),
      )
    }

    // Function to get the surrounding container's width
    const getParentWidth = inject(containerElement, 'cqw:width/')

    /**
     * @type {(() => void)[]}
     */
    const unprovides = []

    // For each container name, provide this element's width and its cqw value
    for (const name of names) {
      const getContainerWidth = elementContentWidths.get(containerElement).get

      const unprovideWidth = provide(containerElement, `cqw:width/${name}`, getContainerWidth)
      const unprovideElement = provide(
        containerElement,
        `cqw:percentage/${name}`,
        derived(() => (getContainerWidth() / getParentWidth()) * 100),
      )
      unprovides.push(unprovideWidth, unprovideElement)
    }

    // Clean up when element is unmounted
    return () => {
      for (const unprovide of unprovides) {
        unprovide()
      }
      observer.unobserve(containerElement)
      elementContentWidths.delete(containerElement)
    }
  })

  // Handle query elements
  select('[data-cqw-query]', queryElement => {
    const queryStrings = queryElement.dataset.cqwQuery.trim().split(' ')

    /**
     * Parsed queries
     * @type {{ name: string, operator: string, value: number }[]}
     */
    const queries = queryStrings
      .map(requestString => {
        const nameParts = requestString.split('/')
        const remainder = nameParts.pop()
        const name = nameParts[0] ?? ''
        const operatorParts = remainder.split(':')
        const value = Number(operatorParts.pop())
        const operator = operatorParts[0] ?? 'gte'

        return { name, operator, value }
      })
      .filter(request => !isNaN(request.value))

    // Watch signals for each query
    for (const query of queries) {
      const cqwSignal = inject(queryElement, `cqw:percentage/${query.name}`)

      watch(
        cqwSignal,
        currentCqw => {
          const attributeName = `data-cqw-match:${query.name}:${query.operator}:${query.value}`

          if (currentCqw === undefined) {
            // No container found
            queryElement.toggleAttribute(attributeName, false)
          } else {
            // Evaluate query

            /**
             * @type {boolean}
             */
            let matches
            switch (query.operator) {
              case 'gte':
                matches = currentCqw >= query.value
                break
              case 'lte':
                matches = currentCqw <= query.value
                break
              case 'gt':
                matches = currentCqw > query.value
                break
              case 'lt':
                matches = currentCqw < query.value
                break
            }
            queryElement.toggleAttribute(attributeName, matches)
          }
        },
        { immediate: true },
      )
    }
  })
}
